import { ReactElement, ReactNode, useEffect, useRef, useState } from "react";
import { buildClasses } from "../utils/buildClasses";
import { keyCodes } from "../utils/KeyCodes";

export function RyeInput({
  id,
  disabled = false,
  autoComplete,
  spellCheck,
  startingText = "",
  placeholderText = "",
  type = "",
  size = "md",
  height = "auto",
  width = "md",
  fontWeight = "normal",
  textAlign = "left",
  focusOnInit = false,
  ackFocus = () => {},
  onChange = () => {},
  onEnter = () => {},
  onEsc = () => {},
  onBlur = () => {},
  error = false,
  trailing,
}: {
  id?: string;
  disabled?: boolean;
  autoComplete?: string;
  spellCheck?: boolean;
  startingText?: string;
  placeholderText?: string;
  type?: "" | "email" | "password";
  size?: "sm" | "md" | "lg";
  height?: "auto" | "full";
  width?: "sm" | "md" | "lg" | "xl" | "full";
  fontWeight?: "light" | "normal" | "heavy";
  textAlign?: "left" | "center" | "right";
  focusOnInit?: boolean;
  ackFocus?: () => void;
  onChange?: (text: string) => void;
  onEnter?: (text: string) => void;
  onEsc?: () => void;
  onBlur?: () => void;
  error?: boolean;
  /**
   * Small controls (e.g. icon buttons) shown inside the input's right edge.
   * The text stops short of them.
   */
  trailing?: ReactNode;
}): ReactElement {
  const [text, setText] = useState(startingText);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (focusOnInit) {
      inputRef.current?.select(); // this takes care of focusing too!
    }
  }, [focusOnInit]);

  // Keep the text in sync with any changes to the input text. One far-fetched
  // reason to make sure we do this is in case someone changes the value
  // on a different device.
  useEffect(() => setText(startingText), [startingText]);

  function handleOnKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.repeat) {
      return;
    }
    switch (e.code) {
      case keyCodes.ESC:
        inputRef.current?.blur();
        onEsc();
        break;
      case keyCodes.ENTER:
        inputRef.current?.blur();
        onEnter(text);
        break;
    }
  }

  function handleInputChanged(e: React.ChangeEvent<HTMLInputElement>) {
    setText(e.target.value);
    onChange(e.target.value);
  }

  const widthClass = buildClasses({
    switch: width,
    cases: new Map([
      ["sm", "w-44"],
      ["md", "w-56"],
      ["lg", "w-72"],
      ["xl", "w-96"],
      ["full", "w-full"],
    ]),
  });

  const input = (
    <input
      id={id}
      disabled={disabled}
      autoComplete={autoComplete}
      spellCheck={spellCheck}
      ref={inputRef}
      type={type}
      placeholder={placeholderText}
      className={buildClasses(
        {
          switch: size,
          cases: new Map([
            ["sm", buildClasses("text-sm")],
            ["md", buildClasses("text-md")],
            ["lg", buildClasses("text-lg")],
          ]),
        },
        {
          switch: height,
          cases: new Map([
            [
              "auto",
              buildClasses({
                switch: size,
                cases: new Map([
                  ["sm", buildClasses("h-7")],
                  ["md", buildClasses("h-9")],
                  ["lg", buildClasses("h-11")],
                ]),
              }),
            ],
            ["full", buildClasses("h-full")],
          ]),
        },
        trailing !== undefined ? "w-full" : widthClass,
        {
          switch: fontWeight,
          cases: new Map([
            ["light", buildClasses("font-light")],
            ["normal", buildClasses("font-normal")],
            ["heavy", buildClasses("font-medium")],
          ]),
        },
        {
          switch: textAlign,
          cases: new Map([
            ["left", "text-left"],
            ["center", "text-center"],
            ["right", "text-right"],
          ]),
        },
        "pl-3",
        // Room for two small icon buttons inside the right edge.
        trailing !== undefined ? "pr-[68px]" : "pr-3",
        "bg-transparent",
        "border",
        "border-on-surface-300",
        "rounded-md",
        "hover:border-on-surface-400",
        {
          if: error,
          then: buildClasses(
            "border-red-300",
            "hover:border-red-500",
            "focus:border-red-500",
          ),
          else: "focus:border-purple",
        },
        "focus:shadow-glow",
        {
          if: error,
          then: "focus:shadow-red-100",
          else: "focus:shadow-purple-100",
        },
        "outline-none",
        "transition-all",
      )}
      value={text}
      onChange={(e) => handleInputChanged(e)}
      onKeyDown={(e) => handleOnKeyDown(e)}
      onBlur={onBlur}
    />
  );

  if (trailing === undefined) {
    return input;
  }
  return (
    <div
      className={buildClasses("relative", "flex", "items-center", widthClass)}
    >
      {input}
      <div
        className={buildClasses(
          "absolute",
          "right-1",
          "flex",
          "items-center",
          "gap-0.5",
        )}
      >
        {trailing}
      </div>
    </div>
  );
}
